import { useState } from "react";
import { CalendarClock, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fdt, label, useApi } from "@/lib/api";

export function StageTimeline({ project, stage, onSaved, canEdit }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ dueAt: "", reason: "" });
  const overdue = stage.dueAt && !stage.isCompleted && new Date(stage.dueAt) < new Date();
  const save = async () => {
    try {
      onSaved((await api.post(`/projects/${project.id}/stages/${stage.id}/reschedule`, { dueAt: new Date(f.dueAt).toISOString(), reason: f.reason })).data);
      toast.success("Stage rescheduled"); setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="stage-timeline">
      {stage.startedAt && <span className="text-muted-foreground">Started {fdt(stage.startedAt)}</span>}
      {stage.dueAt ? <span className={overdue ? "text-rose-600 font-semibold" : "font-medium"} data-testid="stage-due-at">{overdue ? "Overdue · was due" : "Due"} {fdt(stage.dueAt)}</span>
        : <span className="text-muted-foreground">No default timeline</span>}
      {stage.reschedules?.length > 0 && <span className="text-muted-foreground" title={stage.reschedules.map((r) => `${fdt(r.at)} ${r.by}: ${r.reason}`).join("\n")}>Rescheduled {stage.reschedules.length}×</span>}
      {canEdit && !stage.isCompleted && <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(true)} data-testid="reschedule-stage-button"><CalendarClock className="h-3.5 w-3.5 mr-1.5" />Reschedule Stage</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule {stage.stageName}</DialogTitle><DialogDescription>The reason is saved in the project history.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>New due date & time</Label><Input type="datetime-local" value={f.dueAt} onChange={(e) => setF({ ...f, dueAt: e.target.value })} data-testid="reschedule-due-input" /></div>
            <div className="space-y-1.5"><Label>Reason (required)</Label><Textarea rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="reschedule-reason-input" /></div>
          </div>
          <DialogFooter><Button disabled={!f.dueAt || !f.reason.trim()} onClick={save} data-testid="reschedule-save-button">Save new timeline</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function StageBom({ project, stage }) {
  const lists = [stage.showProcurementChecklist && ["Components to procure", project.parts.filter((p) => !p.inStock || p.procurementStatus === "PROCURED")],
    stage.showAssemblyChecklist && ["Components for assembly verification", project.parts]].filter(Boolean);
  return lists.map(([title, parts]) => (
    <div key={title} data-testid={`stage-bom-${title.split(" ")[2]}`}>
      <div className="text-xs text-muted-foreground mb-2">{title}</div>
      <div className="rounded-md border divide-y text-sm">
        {!parts.length ? <div className="px-3 py-2 text-xs text-muted-foreground">Nothing pending, all parts were in stock.</div> : parts.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-3 py-2"><span className="w-28 text-xs text-muted-foreground">{label(p.category)}</span>
            <span className="flex-1">{p.customPartName}</span>
            <span className={`text-xs ${p.inStock ? "text-emerald-600" : "text-amber-600"}`}>{p.procurementStatus === "PROCURED" ? `Procured · ${p.supplier} · Inv ${p.invoiceNumber}` : p.inStock ? (p.serialNumber ? `S/N ${p.serialNumber}` : "In stock") : "To be procured"}</span></div>))}
      </div>
    </div>
  ));
}

export function ProcureDialog({ project, part, onClose, onSaved }) {
  const [f, setF] = useState({ supplier: "", invoiceNumber: "" });
  const save = async () => {
    try { onSaved((await api.patch(`/projects/${project.id}/parts/${part.id}/stock`, f)).data); toast.success("Marked as procured"); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="procure-dialog">
        <DialogHeader><DialogTitle>Mark procured: {part.customPartName}</DialogTitle><DialogDescription>Supplier and invoice details are required.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Supplier</Label><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="procure-supplier-input" /></div>
          <div className="space-y-1.5"><Label>Invoice number</Label><Input value={f.invoiceNumber} onChange={(e) => setF({ ...f, invoiceNumber: e.target.value })} data-testid="procure-invoice-input" /></div>
        </div>
        <DialogFooter><Button disabled={!f.supplier.trim() || !f.invoiceNumber.trim()} onClick={save} data-testid="procure-save-button">Mark procured</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TrackingButton({ project, onSaved }) {
  const copy = async (token, what) => {
    await navigator.clipboard.writeText(`${window.location.origin}/track-build/${token}`).catch(() => {});
    toast.success(`${what} tracking link copied`);
  };
  const ensure = async () => {
    if (project.trackingToken) return copy(project.trackingToken, "Build");
    try { const p = (await api.post(`/projects/${project.id}/tracking-link`)).data; onSaved(p); copy(p.trackingToken, "Build"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" onClick={ensure} data-testid="copy-tracking-link-button"><Link2 className="h-4 w-4 mr-2" />Live tracking link</Button>
      {project.buildCount > 1 && <Button variant="outline" onClick={() => copy(project.orderTrackingToken, "Order")} data-testid="copy-order-tracking-button">All builds link</Button>}
    </>
  );
}

export function BuildSiblings({ project }) {
  const { data } = useApi(project.buildCount > 1 ? "/projects" : null);
  if (!(project.buildCount > 1) || !data) return null;
  const sibs = data.filter((p) => p.orderGroupId === project.orderGroupId).sort((a, b) => a.buildIndex - b.buildIndex);
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 text-xs" data-testid="build-siblings">
      <span className="text-muted-foreground">Order {project.orderCode} · {project.buildCount} builds:</span>
      {sibs.map((p) => <Link key={p.id} to={`/projects/${p.id}`} className={`rounded-full border px-2.5 py-1 font-mono ${p.id === project.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid={`build-link-${p.buildIndex}`}>B{p.buildIndex}</Link>)}
    </div>
  );
}
